/**
 * Omio position ids for the "Find tickets on Omio" link.
 * Integration doc: docs/integrations/omio-impact.md
 * Backend twin: backend/services/omioAffiliateService.js (builds the link).
 *
 * Omio retired its route pages (omio.com/travel/{from}/{to}) for most city
 * pairs in Oct 2026 (HTTP 410, "This page doesn't exist"), so the affiliate
 * deep link now pre-fills Omio's own search form by position id
 * (omio.com/?departure_fk=&arrival_fk=). The ids come from Omio's suggester
 * API, the autocomplete behind their search form: it answers cross-origin
 * reads, while Omio refuses every server-side request (bot protection). So
 * the lookup happens here, in the browser, at the moment of the click, just
 * before the traveller goes to Omio anyway, and the ids ride to the backend
 * with the link request (?fromPositionId= / ?toPositionId=).
 *
 * Every failure is silent: without ids the backend falls back to the route
 * page, and the backend keeps the ids a previous click resolved for the leg.
 */

export const OMIO_SUGGESTER_URL = 'https://www.omio.com/suggester-api/v5/position';
/** The click already opened a blank tab; the traveller is waiting on this. */
const LOOKUP_TIMEOUT_MS = 2500;

/** One end of a leg, as the journey payload has its stop. */
export interface OmioPlace { city: string | null | undefined; country?: string | null }
export interface OmioRoute { from: OmioPlace; to: OmioPlace }
export interface OmioRoutePositions { fromPositionId: number | null; toPositionId: number | null }

/** One suggester result, the fields this module reads (there are many more). */
export interface OmioSuggesterPosition {
  positionId?: number;
  /** The city a station or airport belongs to; equals positionId for a city. */
  locationPositionId?: number;
  type?: string;
  /** ISO 3166-1 alpha-2, e.g. "MA". */
  countryCode?: string;
  /** In the request's locale (en here): "Morocco", "Germany", "USA". */
  countryNameInUserLocale?: string;
}

const norm = (s: unknown): string =>
  String(s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').trim().toLowerCase();

/**
 * "Zurich, Switzerland" → "Zurich": stops sometimes carry the country in the
 * city string; only the part before the first comma is the city (the
 * backend's citySlug does the same).
 */
export function omioSearchTerm(city: string | null | undefined): string {
  return String(city || '').split(',')[0].trim();
}

const idOf = (p: OmioSuggesterPosition): number | null => {
  const id = Number(p.locationPositionId ?? p.positionId);
  return Number.isInteger(id) && id > 0 ? id : null;
};

/**
 * The city-level position among the suggester's results (they come ranked):
 * the first one in the stop's country when the country is known there (Paris
 * FR over Paris TX, Cologne IT over Cologne DE when the stop says Italy),
 * else the first. A station or airport resolves to its city.
 */
export function pickOmioPosition(results: unknown, place: OmioPlace): number | null {
  if (!Array.isArray(results)) return null;
  const candidates = results.filter((p): p is OmioSuggesterPosition => Boolean(p) && typeof p === 'object' && idOf(p) !== null);
  if (!candidates.length) return null;
  const country = norm(place.country);
  const inCountry = country
    ? candidates.find((p) => norm(p.countryNameInUserLocale) === country || norm(p.countryCode) === country)
    : undefined;
  return idOf(inCountry || candidates[0]);
}

// One lookup per city+country per page load: the same leg's two ends are
// asked once however many times the traveller clicks.
const cache = new Map<string, Promise<number | null>>();

/** Tests only. */
export function resetOmioPositionCache(): void {
  cache.clear();
}

async function lookup(term: string, place: OmioPlace, fetchImpl: typeof fetch): Promise<number | null> {
  const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
  const timer = controller ? setTimeout(() => controller.abort(), LOOKUP_TIMEOUT_MS) : null;
  try {
    const query = new URLSearchParams({ term, locale: 'en', hierarchical: 'true' });
    const res = await fetchImpl(`${OMIO_SUGGESTER_URL}?${query.toString()}`, {
      signal: controller ? controller.signal : undefined,
      credentials: 'omit',
    });
    if (!res.ok) throw new Error(`Omio suggester answered ${res.status}`);
    return pickOmioPosition(await res.json(), place);
  } finally {
    if (timer) clearTimeout(timer);
  }
}

/**
 * The Omio position id of a stop's city, or null when Omio does not know the
 * place (Taghazout), the lookup fails or takes too long. Answers are cached
 * for the page's lifetime; failures are not, so the next click tries again.
 */
export async function resolveOmioPositionId(place: OmioPlace, fetchImpl?: typeof fetch): Promise<number | null> {
  const term = omioSearchTerm(place.city);
  const f = fetchImpl || (typeof fetch === 'function' ? fetch : null);
  if (!term || !f) return null;
  const key = `${norm(term)}|${norm(place.country)}`;
  const hit = cache.get(key);
  if (hit) return hit;
  const pending = lookup(term, place, f);
  cache.set(key, pending);
  try {
    return await pending;
  } catch {
    cache.delete(key);
    return null;
  }
}

/** Both ends of a leg, looked up in parallel. Nulls where unknown. */
export async function resolveOmioRoute(route: OmioRoute | null | undefined, fetchImpl?: typeof fetch): Promise<OmioRoutePositions> {
  if (!route) return { fromPositionId: null, toPositionId: null };
  const [fromPositionId, toPositionId] = await Promise.all([
    resolveOmioPositionId(route.from, fetchImpl),
    resolveOmioPositionId(route.to, fetchImpl),
  ]);
  return { fromPositionId, toPositionId };
}

/** The query params the backend's link routes read; only the ids found. */
export function omioPositionParams(positions: OmioRoutePositions): Record<string, string> {
  const params: Record<string, string> = {};
  if (positions.fromPositionId) params.fromPositionId = String(positions.fromPositionId);
  if (positions.toPositionId) params.toPositionId = String(positions.toPositionId);
  return params;
}
